import React, { useState } from 'react';
import { Save, Cancel } from '@mui/icons-material';
import { FAQItem, FAQItemCreate, FAQItemUpdate } from '../services/cms';
import { Language } from '../contexts/LanguageContext';

interface FAQEditorProps {
  faq: FAQItem | null;
  isCreating: boolean;
  onSave: (data: FAQItemCreate | FAQItemUpdate) => Promise<void>;
  onCancel: () => void;
  language: Language;
}

const FAQEditor: React.FC<FAQEditorProps> = ({ faq, isCreating, onSave, onCancel, language }) => {
  const [formData, setFormData] = useState<FAQItemCreate>({
    question_en: faq?.question_en || '',
    question_tr: faq?.question_tr || '',
    answer_en: faq?.answer_en || '',
    answer_tr: faq?.answer_tr || '',
    category: faq?.category || 'general',
    display_order: faq?.display_order || 0,
    is_published: faq?.is_published !== undefined ? faq.is_published : true,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await onSave(formData);
      onCancel(); // Close modal on success
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to save');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white dark:bg-gray-900 rounded-lg max-w-4xl w-full my-8">
        <div className="p-6 border-b border-gray-200 dark:border-gray-800">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
            {isCreating
              ? (language === 'tr' ? 'Yeni SSS Ekle' : 'Add New FAQ')
              : (language === 'tr' ? 'SSS Düzenle' : 'Edit FAQ')}
          </h2>
        </div>

        {error && (
          <div className="mx-6 mt-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
            <p className="text-red-700 dark:text-red-400">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Category */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {language === 'tr' ? 'Kategori' : 'Category'}
            </label>
            <input
              type="text"
              value={formData.category}
              onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              className="w-full px-4 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-gray-900 dark:focus:ring-white"
              required
            />
          </div>

          {/* Question EN */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {language === 'tr' ? 'Soru (İngilizce)' : 'Question (English)'}
            </label>
            <input
              type="text"
              value={formData.question_en}
              onChange={(e) => setFormData({ ...formData, question_en: e.target.value })}
              className="w-full px-4 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-gray-900 dark:focus:ring-white"
              required
            />
          </div>

          {/* Answer EN */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {language === 'tr' ? 'Cevap (İngilizce)' : 'Answer (English)'}
            </label>
            <textarea
              value={formData.answer_en}
              onChange={(e) => setFormData({ ...formData, answer_en: e.target.value })}
              rows={4}
              className="w-full px-4 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-gray-900 dark:focus:ring-white"
              required
            />
          </div>

          {/* Question TR */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {language === 'tr' ? 'Soru (Türkçe)' : 'Question (Turkish)'}
            </label>
            <input
              type="text"
              value={formData.question_tr}
              onChange={(e) => setFormData({ ...formData, question_tr: e.target.value })}
              className="w-full px-4 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-gray-900 dark:focus:ring-white"
              required
            />
          </div>

          {/* Answer TR */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {language === 'tr' ? 'Cevap (Türkçe)' : 'Answer (Turkish)'}
            </label>
            <textarea
              value={formData.answer_tr}
              onChange={(e) => setFormData({ ...formData, answer_tr: e.target.value })}
              rows={4}
              className="w-full px-4 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-gray-900 dark:focus:ring-white"
              required
            />
          </div>

          {/* Display Order */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {language === 'tr' ? 'Sıralama' : 'Display Order'}
            </label>
            <input
              type="number"
              value={formData.display_order}
              onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) || 0 })}
              className="w-full px-4 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-gray-900 dark:focus:ring-white"
            />
          </div>

          {/* Published */}
          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="is_published"
              checked={formData.is_published}
              onChange={(e) => setFormData({ ...formData, is_published: e.target.checked })}
              className="w-4 h-4 text-gray-900 dark:text-white rounded focus:ring-2 focus:ring-gray-900 dark:focus:ring-white"
            />
            <label htmlFor="is_published" className="text-sm font-medium text-gray-700 dark:text-gray-300">
              {language === 'tr' ? 'Yayınla' : 'Publish'}
            </label>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end space-x-4 pt-4 border-t border-gray-200 dark:border-gray-800">
            <button
              type="button"
              onClick={onCancel}
              className="flex items-center space-x-2 px-4 py-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
            >
              <Cancel style={{ width: '20px', height: '20px' }} />
              <span>{language === 'tr' ? 'İptal' : 'Cancel'}</span>
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex items-center space-x-2 px-4 py-2 bg-gray-900 dark:bg-white text-white dark:text-gray-900 rounded-lg hover:bg-gray-800 dark:hover:bg-gray-100 transition-colors disabled:opacity-50"
            >
              <Save style={{ width: '20px', height: '20px' }} />
              <span>{loading ? (language === 'tr' ? 'Kaydediliyor...' : 'Saving...') : (language === 'tr' ? 'Kaydet' : 'Save')}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default FAQEditor;

